import { useState } from "react";
import { Check, Copy, KeyRound, TriangleAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function CopyButton({ value, testId, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setDone(true);
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <Button type="button" variant="secondary" size="sm" onClick={copy} data-testid={testId} className="rounded-full">
      {done ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
      {done ? "Copied" : label}
    </Button>
  );
}

export function KeyRevealDialog({ open, onClose, deviceKey, deviceName }) {
  const dashboardUrl = process.env.REACT_APP_BACKEND_URL;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-lg rounded-2xl" data-testid="device-key-dialog">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><KeyRound className="h-5 w-5" /></div>
          <DialogTitle>Device key for {deviceName}</DialogTitle>
          <DialogDescription>Paste these two values into the Edge Sender's Settings tab on the Windows server.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="eyebrow mb-1.5">Dashboard address</div>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-xl bg-secondary px-3 py-2 text-xs" data-testid="dashboard-address-value">{dashboardUrl}</code>
              <CopyButton value={dashboardUrl} testId="copy-dashboard-address-button" />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-1.5">Device key</div>
            <div className="flex items-start gap-2">
              <code className="min-w-0 flex-1 break-all rounded-xl bg-secondary px-3 py-2 text-xs" data-testid="device-key-value">{deviceKey}</code>
              <CopyButton value={deviceKey || ""} testId="device-key-copy-button" />
            </div>
          </div>
          <div className="flex gap-2 rounded-xl bg-[hsl(var(--warn)/0.1)] p-3 text-xs text-[hsl(var(--warn))]">
            <TriangleAlert className="h-4 w-4 shrink-0" />
            <span>This key is shown only once. If it is lost, rotate it to get a new one. Treat it like a password.</span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-full" data-testid="device-key-dialog-done-button">I've saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
